import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AdultClassRegistrationAndPayment from './AdultClassRegistrationAndPayment';
import Swal from 'sweetalert2';

// --- STYLED COMPONENTS (Clean Light Mode Design for Standalone Course Detail Page) ---

const Container = styled.div`
  background-color: #f8fafc;
  color: #0f172a;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  min-height: 100vh;
  padding: 80px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const DetailContainer = styled.div`
  max-width: 900px;
  width: 100%;
  background: #ffffff;
  padding: 40px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const BackButton = styled.button`
  background: #f1f5f9;
  color: #0f172a;
  border: none;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.2s;

  &:hover {
    background: #e2e8f0;
  }
`;

const Badge = styled.span`
  background-color: #e0f2fe;
  color: #0284c7;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 20px;
  border: 1px solid #bae6fd;
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  flex-wrap: wrap;
  gap: 15px;
`;

const PriceTag = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  color: #0284c7;
`;

const DetailImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
  border-radius: 12px;
`;

const ModuleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    color: #334155;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: '✓';
      color: #0284c7;
      font-weight: bold;
    }
  }
`;

const RegistrationForm = styled.form`
  background: #f8fafc;
  padding: 30px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1.4rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #334155;
  }

  input {
    padding: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #0284c7;
    }
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 0.9rem;
    color: #475569;
    cursor: pointer;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9, #4f46e5);
  color: #ffffff;
  border: none;
  padding: 16px 36px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 10px 25px -5px rgba(14, 165, 233, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -5px rgba(14, 165, 233, 0.5);
  }
`;

const RegisterButton = styled(CTAButton)`
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

// --- REACT COMPONENT FOR COURSE DETAIL PAGE ---

export default function TrainingDetailPage() {
  const [course, setCourse] = useState(null);
const  navigate =useNavigate();
  // Form states
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [agreedToPayment, setAgreedToPayment] = useState(false);
  

  useEffect(() => {
    // Load course data from localStorage based on previous selection
    const storedCourse = localStorage.getItem('selectedCourse');
    if (storedCourse) {
      setCourse(JSON.parse(storedCourse));
    } else {
      // Fallback mock course if accessed directly without selecting one
    //   setCourse({
    //     id: 1,
    //     title: 'Full-Stack Web Development Mastery',
    //     level: 'Intermediate',
    //     duration: '16 Weeks',
    //     price: '$850',
    //     category: 'Online',
    //     description: 'Master modern frontend and backend tech stacks including React, Node.js, Express, and PostgreSQL with live virtual mentorship.',
    //     image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80',
    //     modules: ['Advanced React & Redux Toolkit', 'Node.js & Restful APIs', 'Database Design & ORM', 'Deployment & Serverless Architecture']
    //   });
    }
  }, []);

  const handleBack = () => {
    // Navigate back or to index page route depending on environment setup
navigate('/trainingprograms')
  };

  
const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToPayment || !course) return;
    
    // Show loading indicator
    Swal.fire({
      title: 'Submitting Registration...',
      text: 'Please wait while we save your details.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await fetch('https://elexdondigitalacademy.com/api/register_course.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName,
          email,
          phone,
          courseTitle: course.title,
          courseMode: course.mode,
          price: course.price
        })
      });

      const data = await response.json();

      if (data.success) {
        // Success alert
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: `You have successfully registered for ${course.title}. Ensure to send in your paymet and contact us.`,
          confirmButtonColor: '#3085d6'
        });

        // Reset form fields
        setFullName('');
        setEmail('');
        setPhone('');
        setAgreedToPayment(false);
      } else {
        // Failure alert from server response
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: data.error || 'Please try again.',
          confirmButtonColor: '#d33'
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Network/System error alert
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Please check your connection and try again.',
        confirmButtonColor: '#d33'
      });
    }
  };


  if (!course) {
    return <Container><p>Loading course details...</p></Container>;
  }

  return (
    <Container>
      <DetailContainer>
        <BackButton onClick={handleBack}>← Back to Programs</BackButton>
        
        <DetailHeader>
          <div>
            {/* <Badge>{course.level}</Badge> */}
            <h2 style={{ fontSize: '2.2rem', margin: '10px 0 10px 0', color: '#0f172a' }}>{course.title}</h2>
            <CourseMeta>
              {/* <span>⏱ Duration: {course.duration}</span> */}
              <span>📍 Mode: {course.mode}</span>
             <PriceTag>₦{Number(course.price).toLocaleString()}</PriceTag>
            </CourseMeta>
          </div>
          <DetailImage src={course.image} alt={course.title} />
          <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: '1.6', margin: 0 }}>
            {course.description}
          </p>
        </DetailHeader>

        <div>
          <h3 style={{ color: '#0f172a', marginBottom: '15px', fontSize: '1.2rem' }}>Comprehensive Course Modules:</h3>
          <ModuleList>
            {course.modules.map((mod, index) => (
              <li key={index}>{mod}</li>
            ))}
          </ModuleList>
        </div>

        <RegistrationForm onSubmit={handleRegistrationSubmit}>
          <h3>Registration</h3>
          <p>Fill and submit this form</p>
          <p>Then make the payment using any of the below methods</p>
          {/* <p style={{ fontSize: '0.9rem', color: '#64748b', margin: 0 }}>
            Secure your slot for the <strong>{course.title}</strong> program. Please fill out your details below.
          </p> */}
          
          <FormGroup>
            <label>Full Name</label>
            <input 
              type="text" 
              placeholder="Enter your full name" 
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label>Email Address</label>
            <input 
              type="email" 
              placeholder="Enter your email address" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label>Phone Number</label>
            <input 
              type="tel" 
              placeholder="Enter your phone number" 
              value={phone} 
              onChange={(e) => setPhone(e.target.value)} 
              required 
            />
          </FormGroup>

          <CheckboxGroup>
            <input 
              type="checkbox" 
              id="paymentAgreement" 
              checked={agreedToPayment} 
              onChange={(e) => setAgreedToPayment(e.target.checked)} 
              required 
            />
            <label htmlFor="paymentAgreement">
              I agree that I have to make payment for this training (₦{Number(course.price).toLocaleString()}) to confirm my registration.
            </label>
          </CheckboxGroup>

          <RegisterButton type="submit" disabled={!agreedToPayment}>
            Submit registration
          </RegisterButton>
        </RegistrationForm>
             <AdultClassRegistrationAndPayment amount={course.price}/>
      </DetailContainer>
 
    </Container>
  );
}